import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { buildLdSurveySubmitBodyFromStorage, submitCustomerSurveyTng } from '@/api/customerSurvey';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { isOtherPrevBrandAnswerComplete } from '@/lib/otherPrevBrandAnswerStorage';
import { isOtherPrevBrandComplete } from '@/lib/otherPrevBrandStorage';
import { isOtherProductTypeComplete } from '@/lib/otherProductTypeStorage';
import { isPrevBrandAnswerComplete, readPrevBrandAnswerAnswers } from '@/lib/prevBrandAnswerStorage';
import { isPrevBrandComplete } from '@/lib/prevBrandStorage';
import { surveyChoiceInputClass } from '@/lib/surveyChoiceInput';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import {
  WHY_LD_NOW_REASON_KEYS,
  getSelectedWhyLdNowReasons,
  getWhyLdNowDetail,
  readWhyLdNowAnswers,
  writeWhyLdNowAnswers,
  type WhyLdNowAnswers,
  type WhyLdNowReasonKey,
} from '@/lib/whyLdNowStorage';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const elaborateTextareaClass =
  'min-w-0 w-full resize-none rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

const labelClass = 'text-left text-[15px] font-medium leading-tight text-black sm:text-[16px]';

const hintClass = 'm-0 text-left text-[14px] leading-tight text-black/80 sm:text-[15px]';

function ReasonOption({
  id,
  label,
  checked,
  onToggle,
}: {
  id: string;
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-1.5">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className={surveyChoiceInputClass}
      />
      <span className={labelClass}>{label}</span>
    </label>
  );
}

export default function WhyLdNow() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet, mobileLocalDigits } = useAppSelector(selectOtpDraft);

  const [answers, setAnswers] = useState<WhyLdNowAnswers>(() => readWhyLdNowAnswers());
  const [warning, setWarning] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    writeWhyLdNowAnswers(answers);
  }, [answers]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  if (questionnaireSet === 'B') return <Navigate to="/likelyld" replace />;

  if (!isPrevBrandComplete()) return <Navigate to="/survey/prevbrand" replace />;
  if (!isPrevBrandAnswerComplete()) return <Navigate to="/survey/prevbrandanswer" replace />;

  const smokedOtherProduct = readPrevBrandAnswerAnswers().smokedOtherProduct;
  if (smokedOtherProduct === 'yes') {
    if (!isOtherProductTypeComplete() || !isOtherPrevBrandComplete()) {
      return <Navigate to="/survey/prevbrand-other" replace />;
    }
    if (!isOtherPrevBrandAnswerComplete()) {
      return <Navigate to="/survey/prevbrandanswer-other" replace />;
    }
  } else if (smokedOtherProduct !== 'no') {
    return <Navigate to="/survey/prevbrandanswer" replace />;
  }

  const toggleReason = (key: WhyLdNowReasonKey) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: { checked: !prev[key].checked },
    }));
    setWarning(null);
  };

  const handleSubmit = () => {
    if (getSelectedWhyLdNowReasons(answers).length === 0) {
      setWarning(t.whyLdNow.reasonRequiredWarning);
      return;
    }
    if (!getWhyLdNowDetail(answers)) {
      setWarning(t.whyLdNow.elaborateWarning);
      return;
    }
    writeWhyLdNowAnswers(answers);

    if (!customerUuid) {
      setWarning(t.whyLdNow.sessionExpiredWarning);
      return;
    }

    const body = buildLdSurveySubmitBodyFromStorage(mobileLocalDigits);
    if (!body) {
      setWarning(t.whyLdNow.incompleteWarning);
      return;
    }

    setSubmitting(true);
    setWarning(null);
    void submitCustomerSurveyTng(customerUuid, body)
      .then((result) => {
        writeTngRewardSession({
          pin: result.pin,
          expirydate: result.expirydate,
          value: result.value,
        });
        clearAllLocalStorage();
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setWarning(err instanceof Error ? err.message : t.whyLdNow.submitError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3 pb-4">
              <SurveyFormPanel className="flex flex-col gap-2">
                <p className={surveyQuestionTextClass}>{t.whyLdNow.question}</p>
                <p className={hintClass}>{t.whyLdNow.selectAll}</p>

                {WHY_LD_NOW_REASON_KEYS.map((key) => (
                  <ReasonOption
                    key={key}
                    id={`why-ld-now-${key}`}
                    label={t.prevBrandAnswer[key]}
                    checked={answers[key].checked}
                    onToggle={() => toggleReason(key)}
                  />
                ))}

                <div className="flex flex-col gap-1">
                  <p className={hintClass}>{t.whyLdNow.elaborateAnswer}</p>
                  <textarea
                    id="why-ld-now-elaboration"
                    rows={3}
                    required
                    value={answers.elaboration}
                    onChange={(e) => {
                      setAnswers((prev) => ({ ...prev, elaboration: e.target.value }));
                      setWarning(null);
                    }}
                    className={elaborateTextareaClass}
                  />
                </div>
              </SurveyFormPanel>
            </div>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-4 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" disabled={submitting} onClick={handleSubmit}>
                {t.whyLdNow.submit}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
